/**
 * Extract S3 object key from a virtual-hosted–style URL or return a bare key path.
 * Example: https://bucket.s3.eu-west-1.amazonaws.com/documents/tenant/uuid.pdf → documents/tenant/uuid.pdf
 */
export function extractS3ObjectKeyFromUrl(fileLocation) {
  if (!fileLocation || typeof fileLocation !== 'string') return null;
  const trimmed = fileLocation.trim();
  if (!trimmed) return null;

  if (!/^https?:\/\//i.test(trimmed)) {
    return trimmed.replace(/^\/+/, '');
  }

  try {
    const u = new URL(trimmed);
    let path = u.pathname.startsWith('/') ? u.pathname.slice(1) : u.pathname;
    if (/^s3[.-]/i.test(u.hostname) && u.hostname.includes('amazonaws.com')) {
      const parts = path.split('/').filter(Boolean);
      if (parts.length > 1) {
        return parts.slice(1).join('/');
      }
    }
    return path || null;
  } catch {
    return null;
  }
}

function parseAnalyzeMetaFromLetterHtml(letterHtml) {
  if (!letterHtml || typeof letterHtml !== 'string') return null;
  try {
    const parsed = JSON.parse(letterHtml);
    if (parsed && typeof parsed === 'object' && parsed.analyze) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Build draft-letter evidence from raw history rows (Snowflake-style keys).
 * Prefer AI/manual summary stored in MESSAGE (or analyze.summary in LETTER_HTML).
 */
export function buildEvidenceFromRawHistoryEntries(entries) {
  const seen = new Map();
  for (const item of entries || []) {
    const action = item.ACTION || item.action;
    if (!['FILE_UPLOAD', 'DOCUMENT_UPLOADED'].includes(action)) continue;
    const url = item.FILE_LOCATION || item.file_location;
    if (!url) continue;
    const key = extractS3ObjectKeyFromUrl(url);
    if (!key) continue;
    if (!seen.has(key)) {
      const meta = parseAnalyzeMetaFromLetterHtml(item.LETTER_HTML || item.letter_html);
      const analyze = meta?.analyze || {};
      const note =
        item.MESSAGE ||
        item.message ||
        analyze.summary ||
        undefined;
      const label =
        meta?.suggested_label ||
        analyze.document_type ||
        item.FILE_NAME ||
        item.file_name ||
        'Supporting document';
      seen.set(key, {
        s3_key: key,
        label,
        note: note && String(note).trim() ? String(note).trim() : undefined,
      });
    }
  }
  return Array.from(seen.values());
}
